import { ShapeType } from '../../types';

export const WebGLUtils = {
  parseColor: (hex: string, out: Float32Array, offset: number) => {
    if (!hex || typeof hex !== 'string') {
        out[offset] = 0; out[offset+1] = 0; out[offset+2] = 0;
        return;
    }
    if (hex.startsWith('hsl')) {
        const match = hex.match(/hsl\(\s*(\d+)/);
        if (match) {
            WebGLUtils.parseHsl(parseInt(match[1]), out, offset);
            return;
        }
    }
    let h = hex;
    if (h.startsWith('#')) h = h.slice(1);
    if (h.length === 3) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2];
    const v = parseInt(h, 16);
    if (isNaN(v)) {
        out[offset] = 0; out[offset+1] = 0; out[offset+2] = 0;
    } else {
        out[offset] = ((v >> 16) & 255) / 255.0;
        out[offset + 1] = ((v >> 8) & 255) / 255.0;
        out[offset + 2] = (v & 255) / 255.0;
    }
  },

  parseHsl: (hue: number, out: Float32Array, offset: number) => {
      const h = hue % 360;
      const s = 1.0;
      const l = 0.5;
      const c = (1 - Math.abs(2 * l - 1)) * s;
      const x = c * (1 - Math.abs((h / 60) % 2 - 1));
      const m = l - c / 2;
      let r=0, g=0, b=0;
      if (0 <= h && h < 60) { r=c; g=x; b=0; }
      else if (60 <= h && h < 120) { r=x; g=c; b=0; }
      else if (120 <= h && h < 180) { r=0; g=c; b=x; }
      else if (180 <= h && h < 240) { r=0; g=x; b=c; }
      else if (240 <= h && h < 300) { r=x; g=0; b=c; }
      else if (300 <= h && h < 360) { r=c; g=0; b=x; }
      out[offset] = r+m; out[offset+1] = g+m; out[offset+2] = b+m;
  },

  hexToRgb: (hex: string): number[] => {
      if (hex.startsWith('#')) hex = hex.slice(1);
      if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
      const v = parseInt(hex, 16);
      return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
  },

  getShapeId: (shape: ShapeType): number => {
      switch(shape) {
          case 'CIRCLE': return 0;
          case 'TRIANGLE': return 3;
          case 'SQUARE': return 4;
          case 'PENTAGON': return 5;
          case 'HEXAGON': return 6;
          case 'HEPTAGON': return 7;
          case 'OCTAGON': return 8;
          case 'NONAGON': return 9;
          case 'DECAGON': return 10;
          default: return 0;
      }
  },

  createShader: (gl: WebGL2RenderingContext, type: number, source: string): WebGLShader => {
    const shader = gl.createShader(type)!;
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error(gl.getShaderInfoLog(shader));
      throw new Error('Shader compile failed');
    }
    return shader;
  },

  createProgram: (gl: WebGL2RenderingContext, vsSource: string, fsSource: string): WebGLProgram => {
    const vs = WebGLUtils.createShader(gl, gl.VERTEX_SHADER, vsSource);
    const fs = WebGLUtils.createShader(gl, gl.FRAGMENT_SHADER, fsSource);
    const program = gl.createProgram()!;
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error(gl.getProgramInfoLog(program));
      throw new Error('Program link failed');
    }
    return program;
  },

  updateCommonUniforms: (gl: WebGL2RenderingContext, prog: WebGLProgram, width: number, height: number, camera: { x: number, y: number, scale: number }, zoomLevel: number = 1.0) => {
      const uRes = gl.getUniformLocation(prog, 'u_resolution');
      gl.uniform2f(uRes, width, height);
      
      const dpr = window.devicePixelRatio || 1;
      const uTrans = gl.getUniformLocation(prog, 'u_translate');
      const centerX = width / 2;
      const centerY = height / 2;
      
      const totalScale = camera.scale * zoomLevel;
      const uScaleValue = totalScale * dpr;
      
      const finalTx = camera.x * uScaleValue + centerX;
      const finalTy = camera.y * uScaleValue + centerY;
      
      gl.uniform2f(uTrans, finalTx, finalTy);
      
      const uScale = gl.getUniformLocation(prog, 'u_scale');
      gl.uniform1f(uScale, uScaleValue);
  }
};
